import React, { useState, useEffect, useRef } from 'react';
import SvgPlay from '../SVG/Play';
import SvgPause from '../SVG/Pause';
import SvgArrowDown from '../SVG/ArrowDown';
import SvgArrowUp from '../SVG/ArrowUp';
import { useAudio } from '../../store/AudioPlayer/useAudio';
import './audio-player.scss';
import Track from '../Tracks/Track/TrackMini';
import { store } from '../../connect';

export const parseAndSumTime = (cliptime) => {
  if (!cliptime) return 0;

  // Удаляем миллисекунды (всё что после точки) и разбиваем по двоеточиям
  const timeParts = cliptime.split(".")[0].split(":");

  // Обрабатываем разные форматы (HH:MM:SS или MM:SS)
  let seconds = 0;

  if (timeParts.length === 3) { // Формат HH:MM:SS
    seconds += parseInt(timeParts[0]) * 3600; // часы
    seconds += parseInt(timeParts[1]) * 60;   // минуты
    seconds += parseInt(timeParts[2]);        // секунды
  } else if (timeParts.length === 2) { // Формат MM:SS
    seconds += parseInt(timeParts[0]) * 60;   // минуты
    seconds += parseInt(timeParts[1]);        // секунды
  } else if (timeParts.length === 1) { // Только секунды
    seconds += parseInt(timeParts[0]);
  }

  return seconds;
};
export const secondsToTimeFormat = (totalSeconds) => {
  if (totalSeconds === 0 || isNaN(totalSeconds)) return '00:00';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  // Форматируем с ведущими нулями
  const pad = (num) => num.toString().padStart(2, '0');

  if (hours > 0) {
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  } else {
    return `${pad(minutes)}:${pad(seconds)}`;
  }
};

const AudioPlayerInline = () => {
  const [localProgress, setLocalProgress] = useState(0);
  const [localVolume, setLocalVolume] = useState(1);
  const [displayTime, setDisplayTime] = useState(0);
  const [showList, setShowList] = useState(false)

  const audioRef = useRef(null);
  const [info, updateSlider] = useState({
    slider: 0,
    currentTime: "0:00"
  })
  const { slider, currentTime } = info
  // Используем глобальное управление аудио через Redux
  const {
    updateAudio,
    close,
    pause,
    play,
    currentTrack,
    isPlaying,
    duration,
    autoplay
  } = useAudio();

  // Инициализация и очистка audio элемента
  useEffect(() => {
    audioRef.current = new Audio();
    audioRef.current.volume = localVolume;

    updateAudio(audioRef)

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
        audioRef.current.removeAttribute('src');
        audioRef.current = null;
      }
    };
  }, []);


  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const handleTimeUpdate = () => {
      if (isPlaying && duration > 0 && audio.currentTime) {
        if (audio.duration === audio.currentTime) {
          const audioState = store.getState().audio;

          if (audioState.autoplay?.on) {
            const currentTrackIndex = audioState.autoplay.list.findIndex(el =>
              el.id === audioState.currentTrack?.trackId
            );

            const nextTrack = audioState.autoplay.list[currentTrackIndex + 1];

            console.log({ currentTrackIndex, nextTrack });
            pause();
            play({
              trackId: nextTrack.id,
              name: nextTrack.name,
              duration: nextTrack.cliptime.split(":").splice(1).join(":"),
              filename: nextTrack.filename,
              ...nextTrack, from: 'player'
            })
            return;
          }

          pause();
          return;
        }

        const progress = (audio.currentTime / duration) * 100;
        updateSlider(progress);
        setDisplayTime(audio.currentTime);
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [duration, isPlaying]);


  useEffect(() => {
    if (currentTrack && audioRef.current) {
      if (audioRef.current.src === currentTrack.filename) {
        audioRef.current.play()
      } else {
        audioRef.current.src = currentTrack.filename;
      }
    }
  }, [currentTrack]);

  // Управление воспроизведением/паузой
  useEffect(() => {
    if (!audioRef.current || !currentTrack) return;

    const handlePlay = async () => {
      try {
        if (isPlaying) {
          await audioRef.current.play();
        } else {
          audioRef.current.pause();
        }
      } catch (error) {
        console.error("Playback error:", error);
      }
    };

    handlePlay();
  }, [isPlaying, currentTrack]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const handleTimeUpdate = () => {
      // Добавляем проверку на isPlaying чтобы избежать дёрганий
      if (isPlaying && duration > 0 && audio.currentTime) {
        const progress = (audio.currentTime / duration) * 100;
        // Используем requestAnimationFrame для плавности
        requestAnimationFrame(() => {
          updateSlider({ slider: progress, currentTime: secondsToTimeFormat(audio.currentTime) });
        });
      }
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [duration, isPlaying]); // Добавляем isPlaying в зависимости

  // Синхронизация текущего времени
  useEffect(() => {
    if (audioRef.current && Math.abs(audioRef.current.currentTime - currentTime) > 0.1) {
      audioRef.current.currentTime = currentTime;
    }
  }, [currentTime]);

  const stopAndClose = () => {
    // Останавливаем воспроизведение
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0; // Перематываем в начало
    }

    // Сбрасываем состояние плеера
    if (pause) pause(); // Используем метод pause из useAudio()

    // Закрываем плеер/модальное окно
    if (close) close();

    // Дополнительные действия при необходимости:
    updateSlider({ slider: 0, currentTime: "00:00" });
    // Сбрасываем прогресс
    setDisplayTime(0); // Сбрасываем отображаемое время
  };




  const handleProgressChange = (e) => {
    if (!currentTrack || !audioRef.current) return;

    // Для клика по прогресс-бару
    if (e.type === 'click') {
      const progressBar = e.currentTarget;
      const rect = progressBar.getBoundingClientRect();
      const offsetX = e.clientX - rect.left;
      const percentage = (offsetX / rect.width) * 100;
      const newValue = Math.min(100, Math.max(0, percentage));

      // Обновляем состояние слайдера
      const newTime = (newValue / 100) * duration;

      updateSlider({ slider: newValue, currentTime: secondsToTimeFormat(newTime) });

      // Устанавливаем текущее время аудио
      if (isFinite(newTime)) {
        audioRef.current.currentTime = newTime;
      }
      return;
    }

    // Для оригинального input (если остался)
    const value = parseFloat(e.target.value);
    if (!isNaN(value)) {
      const newTime = (value / 100) * duration;

      updateSlider({ slider: value, currentTime: newTime });
      if (isFinite(newTime)) {
        audioRef.current.currentTime = newTime;
      }
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setLocalVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  if (!isPlaying) {
    return null;
  }

  return (
    <div className="audio-player-container" title={currentTrack.name} style={{ width: '50%' }}>
      <div className="audio-player-inline">
        <div className="audio-player-inline__main-controls">
          <button
            onClick={pause}
            className="audio-player-inline__play-button"
            aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
            disabled={!currentTrack}
          >
            {/* <SvgClose className="icon-play" variant="prim" size="sm" /> */}
            <SvgPause className="icon-play" variant="prim" size="sm" />
          </button>
          {/* <button
            onClick={togglePlay}
            className="audio-player-inline__play-button"
            aria-label={isPlaying ? 'Пауза' : 'Воспроизвести'}
            disabled={!currentTrack}
          >
            {isPlaying ?
              <SvgPause className="icon-play" variant="prim" size="sm" />
              :
              <SvgPlay className="icon-play" variant="prim" size="sm" />
            }
          </button> */}
          <div className="audio-player-inline__progress-container">
            <span className="audio-player-inline__time">{formatTime(displayTime)}</span>
            <div
              className="audio-player-inline__progress-bar"
              onClick={handleProgressChange}
              aria-label="Прогресс трека"
              role="progressbar"
              aria-valuenow={slider}
              aria-valuemin="0"
              aria-valuemax="100"
            >
              <div
                className="audio-player-inline__progress-track"
                style={{ width: `${slider}%` }}
              />
            </div>
            <span className="audio-player-inline__time">{formatTime(duration)}</span>
          </div>
          {autoplay.on && <button
            onClick={() => setShowList(oldValue => !oldValue)}
            className="audio-player-inline__play-button"
            aria-label="Следующая"
          >
            {/* <SvgClose className="icon-play" variant="prim" size="sm" /> */}
            {showList ?
              <SvgArrowUp className="icon-play" variant="prim" size="xs" />
              : <SvgArrowDown className="icon-play" variant="prim" size="xs" />
            }
          </button>}
          {autoplay.list && <div className='ScrollContainer__wrap' style={{ position: 'absolute', width: 210, maxHeight: 250, overflowY: 'auto', left: 460, top: 60, backgroundColor: 'white', zIndex:9999 }}>

            {/* {JSON.stringify(autoplay.list[0])} */}
            {showList && autoplay.list && autoplay.list?.map(el =>
              <Track {...el} play={data => play({ ...data, from: 'player' })} />
            )}
          </div>}
        </div>
      </div>
    </div>
  );
};

export default AudioPlayerInline;